<template>
  <div>
    <b-row class="mb-1">
      <b-col class="d-flex justify-content-between align-items-center flex-wrap">
        <div>
          <h1 class="font-weight-bolder mb-0">
            Notificaciones
          </h1>
          <small class="text-muted">
            Enciende o apaga cada aviso por canal. Los contadores son de los últimos {{ statsWindowDays }} días.
          </small>
        </div>
        <b-button
          variant="outline-primary"
          :disabled="loading"
          @click="loadCatalog"
        >
          <feather-icon icon="RefreshCwIcon" />
          Actualizar
        </b-button>
      </b-col>
    </b-row>

    <b-row class="mb-1">
      <b-col
        v-for="channel in channels"
        :key="channel.key"
        md="4"
      >
        <b-card class="mb-1">
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <h4 class="mb-0">
                {{ channel.label }}
              </h4>
              <small class="text-muted">
                {{ channelTotals[channel.key].enabled }} de {{ channelTotals[channel.key].total }} encendidas
              </small>
            </div>
            <div class="text-right">
              <div class="text-success font-weight-bolder">
                {{ channelTotals[channel.key].sent }} enviadas
              </div>
              <div
                v-if="channelTotals[channel.key].failed"
                class="text-danger"
              >
                {{ channelTotals[channel.key].failed }} fallidas
              </div>
              <div
                v-if="channelTotals[channel.key].skipped"
                class="text-warning"
              >
                {{ channelTotals[channel.key].skipped }} omitidas
              </div>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <b-card class="mb-1">
      <b-row>
        <b-col md="6">
          <b-form-group
            label="Buscar notificación"
            class="mb-0"
          >
            <b-form-input
              v-model.trim="search"
              placeholder="Nombre o descripción"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            label="Dirigida a"
            class="mb-0"
          >
            <b-form-select
              v-model="audienceFilter"
              :options="audienceOptions"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>

    <b-overlay :show="loading">
      <b-card
        v-for="category in filteredCategories"
        :key="category.key"
        :title="category.label"
        class="mb-1"
      >
        <b-table-simple
          responsive
          small
          class="mb-0 notification-matrix"
        >
          <b-thead>
            <b-tr>
              <b-th style="min-width: 260px">
                Notificación
              </b-th>
              <b-th style="min-width: 120px">
                Dirigida a
              </b-th>
              <b-th
                v-for="channel in channels"
                :key="channel.key"
                class="text-center"
                style="min-width: 140px"
              >
                {{ channel.label }}
              </b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr
              v-for="event in category.events"
              :key="event.key"
            >
              <b-td>
                <b-link
                  class="font-weight-bolder"
                  :to="{ name: 'notificationLog', params: { eventKey: event.key, channel: 'todos' } }"
                >
                  {{ event.name }}
                </b-link>
                <div>
                  <small class="text-muted">{{ event.description }}</small>
                </div>
                <b-badge
                  v-if="event.always_on"
                  variant="light-secondary"
                  class="ml-50"
                >
                  Siempre activa
                </b-badge>
              </b-td>
              <b-td>
                <b-badge :variant="audienceVariant(event.audience)">
                  {{ event.audience_label }}
                </b-badge>
              </b-td>
              <b-td
                v-for="channel in channels"
                :key="channel.key"
                class="text-center"
              >
                <template v-if="channelFor(event, channel.key)">
                  <b-form-checkbox
                    :checked="channelFor(event, channel.key).enabled"
                    switch
                    :disabled="event.always_on || savingKey === `${event.key}:${channel.key}`"
                    @change="toggle(event, channel.key, $event)"
                  />
                  <b-link
                    class="d-block small mt-25"
                    :to="{ name: 'notificationLog', params: { eventKey: event.key, channel: channel.key } }"
                  >
                    {{ channelFor(event, channel.key).sent_count }} enviadas
                    <span
                      v-if="channelFor(event, channel.key).failed_count"
                      class="text-danger"
                    >
                      · {{ channelFor(event, channel.key).failed_count }} fallidas
                    </span>
                    <span
                      v-if="channelFor(event, channel.key).skipped_count"
                      class="text-warning"
                    >
                      · {{ channelFor(event, channel.key).skipped_count }} omitidas
                    </span>
                  </b-link>
                </template>
                <span
                  v-else
                  class="text-muted"
                >—</span>
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </b-card>

      <b-card v-if="!loading && !filteredCategories.length">
        <div class="text-center text-muted py-2">
          No hay notificaciones que coincidan con el filtro.
        </div>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BButton, BBadge, BLink, BOverlay, BFormGroup, BFormInput,
  BFormSelect, BFormCheckbox, BTableSimple, BThead, BTbody, BTr, BTh, BTd,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BBadge,
    BLink,
    BOverlay,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormCheckbox,
    BTableSimple,
    BThead,
    BTbody,
    BTr,
    BTh,
    BTd,
  },

  data() {
    return {
      loading: false,
      savingKey: null,
      search: '',
      audienceFilter: null,
    }
  },

  computed: {
    ...mapGetters('notifications', {
      channels: 'notificationChannels',
      categories: 'notificationCategories',
      statsWindowDays: 'notificationStatsWindowDays',
    }),

    audienceOptions() {
      const seen = new Map()
      this.categories.forEach(category => {
        category.events.forEach(event => {
          if (!seen.has(event.audience)) {
            seen.set(event.audience, event.audience_label)
          }
        })
      })
      return [
        { value: null, text: 'Todas' },
        ...Array.from(seen, ([value, text]) => ({ value, text })),
      ]
    },

    filteredCategories() {
      const term = this.search.toLowerCase()
      return this.categories
        .map(category => ({
          ...category,
          events: category.events.filter(event => {
            if (this.audienceFilter && event.audience !== this.audienceFilter) return false
            if (!term) return true
            return `${event.name} ${event.description}`.toLowerCase().includes(term)
          }),
        }))
        .filter(category => category.events.length)
    },

    channelTotals() {
      const totals = {}
      this.channels.forEach(channel => {
        totals[channel.key] = {
          total: 0, enabled: 0, sent: 0, failed: 0, skipped: 0,
        }
      })
      this.categories.forEach(category => {
        category.events.forEach(event => {
          event.channels.forEach(item => {
            const bucket = totals[item.channel]
            if (!bucket) return
            bucket.total += 1
            if (item.enabled) bucket.enabled += 1
            bucket.sent += item.sent_count
            bucket.failed += item.failed_count
            bucket.skipped += item.skipped_count
          })
        })
      })
      return totals
    },
  },

  created() {
    this.loadCatalog()
  },

  methods: {
    async loadCatalog() {
      this.loading = true
      try {
        await this.$store.dispatch('notifications/fetchNotificationCatalog')
      } finally {
        this.loading = false
      }
    },

    channelFor(event, channelKey) {
      return event.channels.find(item => item.channel === channelKey)
    },

    audienceVariant(audience) {
      if (audience === 'admin') return 'light-primary'
      if (audience === 'customer') return 'light-info'
      return 'light-warning'
    },

    async toggle(event, channelKey, enabled) {
      this.savingKey = `${event.key}:${channelKey}`
      try {
        await this.$store.dispatch('notifications/updateNotificationSetting', {
          eventKey: event.key,
          channel: channelKey,
          enabled,
        })
        this.$bvToast.toast(
          `${event.name} — ${enabled ? 'activada' : 'desactivada'} en ${this.channelLabel(channelKey)}`,
          { title: 'Notificaciones', variant: enabled ? 'success' : 'warning', solid: true },
        )
      } finally {
        this.savingKey = null
      }
    },

    channelLabel(channelKey) {
      const channel = this.channels.find(item => item.key === channelKey)
      return channel ? channel.label : channelKey
    },
  },
}
</script>

<style scoped>
.notification-matrix >>> .custom-switch {
  display: inline-block;
  padding-left: 2.5rem;
}
</style>
